import api from './api';

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

const toBool = (value) => value === true || value === 1 || value === '1';

// Laravel accepts 1/0 for tinyint(1) columns
const fromBool = (value) => (value ? 1 : 0);

const toObject = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

// Only send a JSON column when it actually holds something
const fromObject = (value) => {
  if (!value || typeof value !== 'object') return null;
  return Object.keys(value).length ? value : null;
};

const nullIfBlank = (value) => {
  if (value === undefined || value === null) return null;
  const trimmed = typeof value === 'string' ? value.trim() : value;
  return trimmed === '' ? null : trimmed;
};

const unwrap = (response) => response?.data?.data ?? response?.data;

const unwrapList = (response, normalize) => {
  const payload = response?.data?.data;
  // Paginated: { data: { current_page, data: [...], total, last_page } }
  const rawList = Array.isArray(payload) ? payload : payload?.data ?? [];
  return {
    items: rawList.map(normalize),
    total: payload?.total ?? rawList.length,
    page: payload?.current_page ?? 1,
    perPage: payload?.per_page ?? rawList.length,
    totalPages: payload?.last_page ?? 1,
  };
};

/**
 * Pulls a human message out of a Laravel error response.
 * Handles the standard 422 `{ message, errors: { field: [...] } }` envelope.
 */
export const getApiError = (error, fallback = 'Something went wrong') => {
  const data = error?.response?.data;
  if (data?.errors && typeof data.errors === 'object') {
    const first = Object.values(data.errors)[0];
    if (Array.isArray(first) && first[0]) return first[0];
  }
  return data?.message || error?.message || fallback;
};

/** Field-keyed validation errors from a 422, in camelCase-agnostic form. */
export const getApiFieldErrors = (error) => {
  const errors = error?.response?.data?.errors;
  if (!errors || typeof errors !== 'object') return {};
  return Object.keys(errors).reduce((acc, key) => {
    acc[key] = Array.isArray(errors[key]) ? errors[key][0] : String(errors[key]);
    return acc;
  }, {});
};

/* ------------------------------------------------------------------ *
 * Normalizers: API (snake_case) -> UI (camelCase)
 * ------------------------------------------------------------------ */

const normalizeItem = (item = {}) => ({
  id: item.id,
  sectionId: item.section_id,
  title: item.title || '',
  subtitle: item.subtitle || '',
  description: item.description || '',
  icon: item.icon || '',
  image: item.image || '',
  value: item.value || '',
  linkLabel: item.link_label || '',
  linkUrl: item.link_url || '',
  extra: toObject(item.extra),
  sortOrder: item.sort_order ?? 0,
  isActive: toBool(item.status),
  createdAt: item.created_at,
  updatedAt: item.updated_at,
});

const normalizeSection = (section = {}) => ({
  id: section.id,
  pageId: section.page_id,
  sectionKey: section.section_key || '',
  type: section.type || 'rich_text',
  title: section.title || '',
  subtitle: section.subtitle || '',
  body: section.body || '',
  image: section.image || '',
  backgroundImage: section.background_image || '',
  ctaLabel: section.cta_label || '',
  ctaUrl: section.cta_url || '',
  settings: toObject(section.settings),
  sortOrder: section.sort_order ?? 0,
  isActive: toBool(section.status),
  items: (section.items || []).map(normalizeItem),
  createdAt: section.created_at,
  updatedAt: section.updated_at,
});

const normalizePage = (page = {}) => ({
  id: page.id,
  title: page.title || '',
  slug: page.slug || '',
  template: page.template || 'default',
  isActive: toBool(page.status),
  isSystem: toBool(page.is_system),
  metaTitle: page.meta_title || '',
  metaDescription: page.meta_description || '',
  metaKeywords: page.meta_keywords || '',
  ogImage: page.og_image || '',
  canonicalUrl: page.canonical_url || '',
  sortOrder: page.sort_order ?? 0,
  sectionsCount: page.sections_count ?? (Array.isArray(page.sections) ? page.sections.length : null),
  sections: (page.sections || []).map(normalizeSection),
  createdAt: page.created_at,
  updatedAt: page.updated_at,
});

const normalizeMedia = (media = {}) => ({
  id: media.id,
  originalName: media.original_name || media.name || '',
  path: media.path || '',
  url: media.url || '',
  mimeType: media.mime_type || '',
  size: Number(media.size) || 0,
  width: media.width ?? null,
  height: media.height ?? null,
  altText: media.alt_text || '',
  folder: media.folder || '',
  createdAt: media.created_at,
});

const normalizeSetting = (setting = {}, fallbackKey = '') => {
  const key = setting.setting_key || setting.key || fallbackKey;
  return {
    id: setting.id ?? key,
    groupName: setting.group_name || setting.group || 'general',
    settingKey: key,
    value: setting.value ?? '',
    type: setting.type || 'text',
    label: setting.label || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    sortOrder: setting.sort_order ?? 0,
  };
};

/* ------------------------------------------------------------------ *
 * Payload builders: UI (camelCase) -> API (snake_case)
 * ------------------------------------------------------------------ */

const pagePayload = (data = {}) => {
  const payload = {
    title: data.title,
    template: nullIfBlank(data.template) || 'default',
    status: fromBool(data.isActive),
    meta_title: nullIfBlank(data.metaTitle),
    meta_description: nullIfBlank(data.metaDescription),
    meta_keywords: nullIfBlank(data.metaKeywords),
    og_image: nullIfBlank(data.ogImage),
    canonical_url: nullIfBlank(data.canonicalUrl),
    sort_order: Number(data.sortOrder) || 0,
  };
  // System page slugs are immutable server-side; never send one back for them.
  if (!data.isSystem && nullIfBlank(data.slug)) {
    payload.slug = data.slug.trim();
  }
  return payload;
};

const sectionPayload = (data = {}) => ({
  section_key: data.sectionKey?.trim(),
  type: data.type,
  title: nullIfBlank(data.title),
  subtitle: nullIfBlank(data.subtitle),
  body: nullIfBlank(data.body),
  image: nullIfBlank(data.image),
  background_image: nullIfBlank(data.backgroundImage),
  cta_label: nullIfBlank(data.ctaLabel),
  cta_url: nullIfBlank(data.ctaUrl),
  settings: fromObject(data.settings),
  sort_order: Number(data.sortOrder) || 0,
  status: fromBool(data.isActive),
});

const itemPayload = (data = {}) => ({
  title: nullIfBlank(data.title),
  subtitle: nullIfBlank(data.subtitle),
  description: nullIfBlank(data.description),
  icon: nullIfBlank(data.icon),
  image: nullIfBlank(data.image),
  value: nullIfBlank(data.value),
  link_label: nullIfBlank(data.linkLabel),
  link_url: nullIfBlank(data.linkUrl),
  extra: fromObject(data.extra),
  sort_order: Number(data.sortOrder) || 0,
  status: fromBool(data.isActive),
});

/* ------------------------------------------------------------------ *
 * Pages
 * ------------------------------------------------------------------ */

export const pageService = {
  getAll: async (params = {}) => {
    const apiParams = {
      page: params.page || 1,
      per_page: params.limit || 10,
      ...(params.search ? { search: params.search } : {}),
      ...(params.status !== undefined && params.status !== '' ? { status: params.status } : {}),
    };
    const response = await api.get('/pages', { params: apiParams });
    const { items, total, page, totalPages } = unwrapList(response, normalizePage);
    return { data: { pages: items, total, page, totalPages } };
  },

  getById: async (id) => {
    const response = await api.get(`/pages/${id}`);
    return { data: normalizePage(unwrap(response)) };
  },

  create: async (data) => {
    const response = await api.post('/pages', pagePayload(data));
    return { data: normalizePage(unwrap(response)) };
  },

  update: async (id, data) => {
    const response = await api.put(`/pages/${id}`, pagePayload(data));
    return { data: normalizePage(unwrap(response)) };
  },

  delete: async (id) => api.delete(`/pages/${id}`),

  toggleStatus: async (id) => {
    const response = await api.patch(`/pages/${id}/toggle-status`);
    return { data: normalizePage(unwrap(response)) };
  },

  reorder: async (ids) => api.post('/pages/reorder', { ids }),
};

/* ------------------------------------------------------------------ *
 * Page sections
 * ------------------------------------------------------------------ */

export const sectionService = {
  getByPage: async (pageId) => {
    const response = await api.get(`/pages/${pageId}/sections`);
    const payload = response?.data?.data;
    const list = Array.isArray(payload) ? payload : payload?.data ?? [];
    return { data: list.map(normalizeSection) };
  },

  getById: async (id) => {
    const response = await api.get(`/sections/${id}`);
    return { data: normalizeSection(unwrap(response)) };
  },

  create: async (pageId, data) => {
    const response = await api.post(`/pages/${pageId}/sections`, sectionPayload(data));
    return { data: normalizeSection(unwrap(response)) };
  },

  update: async (id, data) => {
    const response = await api.put(`/sections/${id}`, sectionPayload(data));
    return { data: normalizeSection(unwrap(response)) };
  },

  delete: async (id) => api.delete(`/sections/${id}`),

  toggleStatus: async (id) => {
    const response = await api.patch(`/sections/${id}/toggle-status`);
    return { data: normalizeSection(unwrap(response)) };
  },

  reorder: async (pageId, ids) => api.post(`/pages/${pageId}/sections/reorder`, { ids }),
};

/* ------------------------------------------------------------------ *
 * Section items
 * ------------------------------------------------------------------ */

export const sectionItemService = {
  getBySection: async (sectionId) => {
    const response = await api.get(`/sections/${sectionId}/items`);
    const payload = response?.data?.data;
    const list = Array.isArray(payload) ? payload : payload?.data ?? [];
    return { data: list.map(normalizeItem) };
  },

  create: async (sectionId, data) => {
    const response = await api.post(`/sections/${sectionId}/items`, itemPayload(data));
    return { data: normalizeItem(unwrap(response)) };
  },

  update: async (id, data) => {
    const response = await api.put(`/items/${id}`, itemPayload(data));
    return { data: normalizeItem(unwrap(response)) };
  },

  delete: async (id) => api.delete(`/items/${id}`),

  toggleStatus: async (id) => {
    const response = await api.patch(`/items/${id}/toggle-status`);
    return { data: normalizeItem(unwrap(response)) };
  },

  reorder: async (sectionId, ids) => api.post(`/sections/${sectionId}/items/reorder`, { ids }),
};

/* ------------------------------------------------------------------ *
 * Site settings
 * ------------------------------------------------------------------ */

// Preferred tab order; anything unknown is appended alphabetically.
const GROUP_ORDER = ['general', 'contact', 'social', 'seo', 'footer', 'scripts'];

/**
 * The API returns settings grouped by group_name. Shapes seen in the wild:
 *   { contact: [ {...setting} ] }            grouped arrays
 *   { contact: { company_phone: {...} } }    grouped maps
 *   [ {...setting} ]                         flat list
 * All three collapse to the same normalized group list here.
 */
const normalizeSettingsResponse = (payload) => {
  const flat = [];

  if (Array.isArray(payload)) {
    payload.forEach((setting) => flat.push(normalizeSetting(setting)));
  } else if (payload && typeof payload === 'object') {
    Object.entries(payload).forEach(([groupName, groupValue]) => {
      if (Array.isArray(groupValue)) {
        groupValue.forEach((setting) => flat.push({ ...normalizeSetting(setting), groupName: setting.group_name || groupName }));
      } else if (groupValue && typeof groupValue === 'object') {
        Object.entries(groupValue).forEach(([key, setting]) => {
          const normalized =
            setting && typeof setting === 'object' && !Array.isArray(setting)
              ? normalizeSetting(setting, key)
              : normalizeSetting({ setting_key: key, value: setting }, key);
          flat.push({ ...normalized, groupName: normalized.groupName || groupName });
        });
      }
    });
  }

  const groupNames = [...new Set(flat.map((s) => s.groupName))].sort((a, b) => {
    const ai = GROUP_ORDER.indexOf(a);
    const bi = GROUP_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  const groups = groupNames.map((name) => ({
    name,
    settings: flat
      .filter((s) => s.groupName === name)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.settingKey.localeCompare(b.settingKey)),
  }));

  const byKey = flat.reduce((acc, setting) => {
    acc[setting.settingKey] = setting.value;
    return acc;
  }, {});

  return { groups, settings: flat, byKey };
};

export const settingsService = {
  getAll: async (group) => {
    // with_meta returns each setting's real label and type instead of a bare
    // key => value map, so the form reads "MW Renewable Energy Experience"
    // rather than a title-cased version of the key.
    const response = await api.get('/settings', {
      params: { with_meta: 1, ...(group ? { group } : {}) },
    });
    return { data: normalizeSettingsResponse(response?.data?.data ?? response?.data) };
  },

  /** Bulk upsert — accepts a flat { setting_key: value } map. */
  update: async (settings) => {
    const response = await api.put('/settings', { settings });
    return { data: normalizeSettingsResponse(response?.data?.data ?? response?.data) };
  },
};

/* ------------------------------------------------------------------ *
 * Media library
 * ------------------------------------------------------------------ */

export const mediaService = {
  getAll: async (params = {}) => {
    const apiParams = {
      page: params.page || 1,
      per_page: params.limit || 24,
      ...(params.search ? { search: params.search } : {}),
      ...(params.folder ? { folder: params.folder } : {}),
    };
    const response = await api.get('/media', { params: apiParams });
    const { items, total, page, totalPages, perPage } = unwrapList(response, normalizeMedia);
    return { data: { media: items, total, page, totalPages, perPage } };
  },

  upload: async (file, { folder = 'cms', altText = '', onProgress } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (folder) formData.append('folder', folder);
    if (altText) formData.append('alt_text', altText);

    const response = await api.post('/media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded * 100) / event.total));
        }
      },
    });
    return { data: normalizeMedia(unwrap(response)) };
  },

  delete: async (id) => api.delete(`/media/${id}`),
};

export const cmsService = {
  pages: pageService,
  sections: sectionService,
  items: sectionItemService,
  settings: settingsService,
  media: mediaService,
};

export default cmsService;
